import api from '@api-client/client';
import { useQuery } from '@tanstack/react-query';
import { ListDataResponse } from '@api-client/types';
import SearchResponse from '@/interfaces/SearchResponse.interface';
import { useEffect, useState } from 'react';

// TODO: Remove this mock when backend is ready - set to false or delete this entire block
const USE_MOCK_DATA = true;

function getMockSearchResults(
  searchTerm: string
): Promise<ListDataResponse<SearchResponse>> {
  // Simulate API delay
  return new Promise((resolve) => {
    setTimeout(() => {
      const lowerSearchTerm = searchTerm.toLowerCase();

      const mockResults: SearchResponse[] = [
        // Companies
        {
          name: ` Corporation Ltd`,
          type: 'company',
          url: '/companies/1'
        },
        {
          name: ` Holdings Inc`,
          type: 'company',
          url: '/companies/2'
        },
        {
          name: ` Group PLC`,
          type: 'company',
          url: '/companies/3'
        },
        {
          name: ` Industries`,
          type: 'company',
          url: '/companies/4'
        },
        {
          name: ` Solutions Ltd`,
          type: 'company',
          url: '/companies/5'
        },
        {
          name: ` Partners`,
          type: 'company',
          url: '/companies/6'
        },
        {
          name: ` Ventures`,
          type: 'company',
          url: '/companies/7'
        },
        {
          name: ` Capital Group`,
          type: 'company',
          url: '/companies/8'
        },
        // Individuals
        {
          name: ` Smith`,
          type: 'individual',
          url: '/individual/1'
        },
        {
          name: ` Johnson`,
          type: 'individual',
          url: '/individual/2'
        },
        {
          name: ` Williams`,
          type: 'individual',
          url: '/individual/3'
        },
        {
          name: ` Brown`,
          type: 'individual',
          url: '/individual/4'
        },
        {
          name: ` Jones`,
          type: 'individual',
          url: '/individual/5'
        },
        {
          name: ` Davis`,
          type: 'individual',
          url: '/individual/6'
        },
        {
          name: ` Miller`,
          type: 'individual',
          url: '/individual/7'
        },
        {
          name: ` Wilson`,
          type: 'individual',
          url: '/individual/8'
        },
        {
          name: ` Moore`,
          type: 'individual',
          url: '/individual/9'
        },
        {
          name: ` Taylor`,
          type: 'individual',
          url: '/individual/10'
        },
        // Tasks
        {
          name: `Review  proposal`,
          type: 'task',
          url: '/tasks/1'
        },
        {
          name: `Follow up with `,
          type: 'task',
          url: '/tasks/2'
        },
        {
          name: `Prepare  documentation`,
          type: 'task',
          url: '/tasks/3'
        },
        {
          name: `Schedule meeting for `,
          type: 'task',
          url: '/tasks/4'
        },
        {
          name: `Update  records`,
          type: 'task',
          url: '/tasks/5'
        },
        {
          name: `Contact  regarding quote`,
          type: 'task',
          url: '/tasks/6'
        },
        {
          name: `Process  application`,
          type: 'task',
          url: '/tasks/7'
        },
        {
          name: `Send  contract`,
          type: 'task',
          url: '/tasks/8'
        },
        {
          name: `Complete  onboarding`,
          type: 'task',
          url: '/tasks/9'
        },
        {
          name: `Verify  information`,
          type: 'task',
          url: '/tasks/10'
        },
        // Products
        {
          name: ` Insurance Plan`,
          type: 'product',
          url: '/products/1'
        },
        {
          name: ` Premium Coverage`,
          type: 'product',
          url: '/products/2'
        },
        {
          name: ` Business Policy`,
          type: 'product',
          url: '/products/3'
        },
        {
          name: ` Protection Package`,
          type: 'product',
          url: '/products/4'
        },
        {
          name: ` Comprehensive Plan`,
          type: 'product',
          url: '/products/5'
        },
        {
          name: ` Essential Coverage`,
          type: 'product',
          url: '/products/6'
        },
        {
          name: ` Professional Policy`,
          type: 'product',
          url: '/products/7'
        },
        {
          name: ` Enterprise Solution`,
          type: 'product',
          url: '/products/8'
        },
        {
          name: ` Standard Plan`,
          type: 'product',
          url: '/products/9'
        },
        {
          name: ` Advanced Coverage`,
          type: 'product',
          url: '/products/10'
        }
      ];

      resolve({
        items: mockResults,
        pagination: {
          page: 1,
          perPage: 10,
          total: mockResults.length
        }
      });
    }, 300); // Simulate network delay
  });
}

interface UseGlobalSearchParams {
  searchTerm: string;
  enabled?: boolean;
  debounceTime?: number;
}

export function useGlobalSearch({
  searchTerm,
  enabled = true,
  debounceTime = 500
}: UseGlobalSearchParams) {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceTime);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceTime]);

  return useQuery<ListDataResponse<SearchResponse>>({
    queryKey: ['global-search', debouncedSearchTerm],
    queryFn: () => api.globalSearch.search(debouncedSearchTerm),
    enabled: enabled && debouncedSearchTerm.length > 0
  });
}
